<template>
  <div class="fts-profile-badge">
    <fts-tippy
      :options="{ theme: 'light-border', interactive: true }"
      appendBoxClass="profile-badge-box"
      append-content-class="profile-badge-content"
    >
      <component :is="tag" slot="trigger" class="profile-badge">
        <avatar
          :username="profileUsername || ''"
          :src="profilePhoto"
          :size="photoSize"
          :custom-style="avatarStyle"
          :background-color="!profileName ? 'var(--fts-disabled)' : null"
          class="profile-badge-avatar"
          :class="appendPhotoClass"
        />
        <div
          class="profile-badge-name"
          :class="appendNameClass"
          v-if="!photoMode"
        >
          <slot
            name="profileName"
            :profile="profile"
            :profileUsername="profileUsername"
            :profileName="profileName"
            :profilePhoto="profilePhoto"
          >
            {{profileName}}
          </slot>
        </div>
      </component>
      <template #content>
        <div class="profile-badge-content-header">
          <avatar
            :username="profileUsername || ''"
            :src="profilePhoto"
            :size="56"
            :custom-style="avatarContentStyle"
            :background-color="!profileName ? 'var(--fts-disabled)' : null"
            class="profile-badge-content-avatar"
          />
        </div>
        <div class="profile-badge-content-wrapper">
          <div class="profile-badge-content-name" v-if="profileName">{{profileName}}</div>
          <div class="profile-badge-content-username" v-if="profileUsername">{{profileUsername}}</div>
          <div class="profile-badge-content-disabled" v-if="isDisabled">
            this user does not exist or has been disabled.
          </div>
        </div>
      </template>
    </fts-tippy>
  </div>
</template>

<script>
import Avatar from "vue-avatar";

import FTSTippy from "./FTSTippy";
import commons from '../commons/commons';
import webAppConfig from '../commons/webApp.config';

export default {
  components: {
    'avatar': Avatar,
    'fts-tippy': FTSTippy
  },
  props: {
    tag: {
      type: String,
      default: 'div'
    },
    users: Array,
    user: Object,
    username: String,
    profileId: String,
    /**
     * only show profile photo
     */
    photoMode: Boolean,
    photoSize: {
      type: Number,
      default: 20
    },
    /**
     * show only if profile not found
     */
    fallbackName: String,
    appendPhotoClass: String,
    appendNameClass: String,
  },
  data: () => ({
    loaded: false,
  }),
  methods: {
    loadImage() {
      if (this.profilePhoto) {
        commons.imageLoader(
          this.profilePhoto, 
          () => { this.loaded = true; },
          () => { this.loaded = false; }
        );
      } else {
        this.loaded = false;
      }
    },
    getAvatarStyle(photoSize) {
      const DEFAULT_PROFILE_IMAGE = `url('${commons.defaultProfileImage}') no-repeat scroll 0% 0% / ${photoSize}px ${photoSize}px content-box border-box`;
      const DEFAULT_STYLE = {
        display: 'flex',
        width: `${photoSize}px`,
        height: `${photoSize}px`,
        minWidth: `${photoSize}px`,
        minHeight: `${photoSize}px`,
        borderRadius: '50%',
        lineHeight: `${(photoSize + Math.floor(photoSize / photoSize))}px`,
        fontWeight: 'bold',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        userSelect: 'none',
      }

      if (this.profilePhoto) {
        return {
          ...DEFAULT_STYLE,
          background: this.loaded
            ? `url('${this.profilePhoto}') no-repeat scroll 0% 0% / ${photoSize}px ${photoSize}px content-box border-box`
            : DEFAULT_PROFILE_IMAGE
        }
      } else if (this.profileUsername) {
        return DEFAULT_STYLE;
      } else {
        return { ...DEFAULT_STYLE, background: DEFAULT_PROFILE_IMAGE };
      }
    }
  },
  computed: {
    profile() {
      try {
        if (this.user) {
          return this.user;
        } else if (this.username) { 
          const res = this.users.find(x =>
            x.username === this.username
            || x.userName === this.username
          );
          if (res) return res;
        } else if (this.profileId) {
          const res = this.users.find(x => x.profileId === this.profileId);
          if (res) return res;
        }
      } catch(err) {
        console.error(err);
      }
      return {};
    },
    profileUsername() {
      return this.username ? this.username : (this.profile.username || this.profile.userName);
    },
    profileName() {
      return this.profile.displayName || this.profileUsername || this.fallbackName;
    },
    profilePhoto() {
      if (this.profile.profilePictureUrl) {
        return webAppConfig.baseURL && this.profile.profilePictureUrl.startsWith('/')
          ? `${webAppConfig.baseURL}${this.profile.profilePictureUrl}`
          : this.profile.profilePictureUrl;
      } else if (this.profileUsername === 'FETIAS-Bot') {
        return commons.fetiasLogo;
      }
    },
    avatarStyle() {
      return this.getAvatarStyle(this.photoSize);
    },
    avatarContentStyle() {
      return this.getAvatarStyle(56);
    },
    isDisabled() {
      return (
        this.profile.displayName === null
        || this.profile.displayName === undefined
      )
      && (
        this.profileUsername === null
        || this.profileUsername === undefined
      );
    }
  },
  beforeUpdate() {
    this.loadImage();
  },
  mounted() {
    this.loadImage();
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-profile-badge {
  .profile-badge {
    display: flex;
    align-items: center;
  }
  .profile-badge-avatar {
    & + .profile-badge-name {
      margin-left: 0.25rem;
    }
  }
  .profile-badge-name {
    @include text-overflow-ellipsis;
    font-size: 0.875rem;
    cursor: default;
  }
}
/* tippy content */
.profile-badge-box {
  min-width: 180px;
  padding: 0;
  border-radius: 0.5rem;
  overflow-wrap: break-word;
  .profile-badge-content {
    text-align: center;
  }
  .profile-badge-content-header  {
    padding-top: 1.25rem;
  }
  .profile-badge-content-wrapper {
    padding: 0.75rem 1rem 1.25rem;
  }
  .profile-badge-content-avatar {
    margin-left: auto;
    margin-right: auto;
    box-shadow: var(--fts-elevation-2);
  }
  .profile-badge-content-name {
    color: var(--fts-text);
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--fts-text);
    & + .profile-badge-content-username {
      margin-top: 0.125rem;
    }
  }
  .profile-badge-content-username {
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--fts-default);
  }
  .profile-badge-content-disabled {
    margin-top: 0.25rem;
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--fts-danger);
  }
}
</style>